import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { BookOpen, Play, ChevronRight } from 'lucide-react';
import ProductLimitationsNotice from '../components/ProductLimitationsNotice';
import AdrenalInsufficiencyResources from '../components/AdrenalInsufficiencyResources';

const GuidesPage = () => {
  const { language } = useLanguage();
  const fr = language === 'fr';

  const guides = [
    {
      slug: '/valto',
      en: {
        title: 'Valtoco\u00ae (Diazepam Nasal Spray)',
        category: 'Seizure Rescue Medication',
        description: 'Official manufacturer resources for Valtoco\u00ae 5 mg, 10 mg, 15 mg and 20 mg doses.',
        product: 'ValtoPod',
      },
      fr: {
        title: 'Valtoco\u00ae (Diaz\u00e9pam en vaporisateur nasal)',
        category: 'M\u00e9dicament anti-crise \u00e9pileptique',
        description: 'Ressources officielles du fabricant pour Valtoco\u00ae 5 mg, 10 mg, 15 mg et 20 mg.',
        product: 'ValtoPod',
      },
    },
    {
      slug: '/nayzi',
      en: {
        title: 'Nayzilam\u00ae (Midazolam Nasal Spray)',
        category: 'Seizure Rescue Medication',
        description: 'Official manufacturer resources for Nayzilam\u00ae 5 mg dose.',
        product: 'NayziPod',
      },
      fr: {
        title: 'Nayzilam\u00ae (Midazolam en vaporisateur nasal)',
        category: 'M\u00e9dicament anti-crise \u00e9pileptique',
        description: 'Ressources officielles du fabricant pour Nayzilam\u00ae 5 mg.',
        product: 'NayziPod',
      },
    },
    {
      slug: '/project-act',
      en: {
        title: 'Project ACT\u2122 \u2014 Adrenal Crisis Transport',
        category: 'Adrenal Emergency Kit',
        description: 'Complete resource page for the CortoPod ACT\u2122 lineup. Case setup, card holder assembly, fit check, and safety information.',
        product: 'CortoPod ACT\u2122',
      },
      fr: {
        title: 'Project ACT\u2122 \u2014 Transport en crise surr\u00e9nalienne',
        category: 'Kit d\u2019urgence surr\u00e9nalien',
        description: 'Page de ressources compl\u00e8te pour la gamme CortoPod ACT\u2122. Configuration de l\u2019\u00e9tui, assemblage de la carte, v\u00e9rification d\u2019ajustement et informations de s\u00e9curit\u00e9.',
        product: 'CortoPod ACT\u2122',
      },
    },
  ];

  return (
    <main className="pt-20 sm:pt-24">
      {/* Hero */}
      <section className="bg-[#1a1a1a] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 text-[#E31B23] font-bold text-sm uppercase tracking-widest mb-4">
              <BookOpen className="w-4 h-4" />
              {fr ? 'Référence' : 'Reference'}
            </span>
            <h1 className="font-outfit font-black text-4xl sm:text-5xl tracking-tight text-white mb-6">
              {fr ? 'Configuration des \u00e9tuis et ressources officielles des fabricants' : 'Case Setup & Official Manufacturer Resources'}
            </h1>
            <p className="font-manrope text-lg text-neutral-300">
              {fr
                ? 'Cette section contient des renseignements sur la configuration physique des \u00e9tuis et des liens vers des ressources externes publi\u00e9es par les fabricants de m\u00e9dicaments ou des organismes de sant\u00e9 reconnus. MyGuardPod ne fournit pas d\u2019instructions d\u2019administration, de protocoles m\u00e9dicaux, de formation ni de conseils cliniques.'
                : 'This section contains physical case-setup information and links to external resources published by medication manufacturers or recognized health organizations. MyGuardPod does not provide medication-administration instructions, medical protocols, training or clinical guidance.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {guides.map((guide, index) => {
              const content = fr ? guide.fr : guide.en;
              const CardWrapper = guide.slug ? Link : 'div';
              const cardProps = guide.slug ? { to: guide.slug } : {};

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <CardWrapper
                    {...cardProps}
                    className={`block bg-neutral-50 rounded-2xl p-8 h-full transition-all duration-300 ${
                      guide.slug ? 'hover:shadow-lg hover:border-[#E31B23] border-2 border-transparent cursor-pointer group' : 'opacity-70 border-2 border-dashed border-neutral-300'
                    }`}
                    data-testid={`guide-${index}`}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      {guide.slug ? (
                        <Play className="w-5 h-5 text-[#E31B23]" />
                      ) : (
                        <BookOpen className="w-5 h-5 text-neutral-400" />
                      )}
                      <span className="font-manrope text-xs uppercase tracking-widest text-neutral-500">
                        {content.category}
                      </span>
                    </div>

                    <h2 className={`font-outfit font-bold text-xl mb-3 ${guide.slug ? 'text-[#1a1a1a] group-hover:text-[#E31B23]' : 'text-neutral-500'} transition-colors`}>
                      {content.title}
                    </h2>

                    <p className="font-manrope text-neutral-600 text-sm leading-relaxed mb-6">
                      {content.description}
                    </p>

                    <div className="mt-auto flex items-center justify-between">
                      <span className="font-outfit font-bold text-sm text-[#E31B23]">
                        {content.product}
                      </span>
                      {guide.slug ? (
                        <ChevronRight className="w-5 h-5 text-neutral-400 group-hover:text-[#E31B23] transition-colors" />
                      ) : (
                        <span className="font-manrope text-xs text-neutral-400 italic">
                          {fr ? 'À venir' : 'Coming soon'}
                        </span>
                      )}
                    </div>
                  </CardWrapper>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* External content disclaimer + Limitations Notice */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="bg-white border-l-4 border-[#E31B23] rounded-r-2xl p-6">
            <p className="font-manrope text-sm text-neutral-700 leading-relaxed">
              <span className="font-bold text-[#1a1a1a]">
                {fr ? 'Information m\u00e9dicale externe\u00a0: ' : 'External medical information: '}
              </span>
              {fr
                ? 'ce contenu a \u00e9t\u00e9 cr\u00e9\u00e9 et publi\u00e9 par la source indiqu\u00e9e, et non par MyGuardPod. Respectez votre prescription, les instructions de votre professionnel de la sant\u00e9 et votre plan d\u2019urgence personnel.'
                : 'this content was created and published by its identified source, not by MyGuardPod. Follow your prescription, your healthcare professional\u2019s instructions and your personal emergency plan.'}
            </p>
          </div>
          <ProductLimitationsNotice />
        </div>
      </section>

      {/* Independent Adrenal Insufficiency Resources */}
      <section className="py-16 bg-white">
        <AdrenalInsufficiencyResources />
      </section>
    </main>
  );
};

export default GuidesPage;
